import React from "react";

import { DataValue } from "src/base-components/DataList";
import { UrlValue } from "src/base-components/UrlValue";
import { Pill } from "src/design-system/Pill";
import {
  CoordinatesPropertyValue,
  FilePropertyValue,
  IpAddressPropertyValue,
} from "src/entities/queries";
import { PROPERTY_TYPES_TO_ICONS } from "src/eventsCatalogue/SchemaEditor/utils";
import { isUrlValue } from "src/utils/url";

// Type guards for backwards compatibility with old string-format values

export const isIpAddressObject = (
  value: unknown,
): value is IpAddressPropertyValue =>
  typeof value === "object" &&
  value !== null &&
  (typeof (value as IpAddressPropertyValue).ipv4 === "string" ||
    typeof (value as IpAddressPropertyValue).ipv6 === "string");

export const isLatLongObject = (
  value: unknown,
): value is CoordinatesPropertyValue =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as CoordinatesPropertyValue).lat === "number" &&
  typeof (value as CoordinatesPropertyValue).long === "number";

export const isFileObject = (value: unknown): value is FilePropertyValue =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as FilePropertyValue).document_url === "string";

// Renderers

export const renderIpAddress = (propertyValue: IpAddressPropertyValue) => (
  <div className="flex h-5 items-center font-code-12">
    <DataValue field="" value={propertyValue.ipv4 ?? propertyValue.ipv6} />
  </div>
);

export const renderLatLong = (propertyValue: CoordinatesPropertyValue) => (
  <DataValue field="" value={propertyValue.lat + ", " + propertyValue.long} />
);

export const renderFile = (propertyValue: FilePropertyValue) => (
  <Pill size="sm" variant="gray">
    <Pill.Icon icon={PROPERTY_TYPES_TO_ICONS.file} />
    <Pill.Text>
      <div className="max-w-36">
        <DataValue
          field=""
          maxDisplayLines={1}
          value={propertyValue.name || propertyValue.document_url}
        />
      </div>
    </Pill.Text>
  </Pill>
);

// Convenience wrappers that handle both old string and new object formats

export const renderIpAddressValue = (value: unknown): React.ReactNode => {
  if (isIpAddressObject(value)) return renderIpAddress(value);
  return String(value);
};

export const renderLatLongValue = (value: unknown): React.ReactNode => {
  if (isLatLongObject(value)) return renderLatLong(value);
  return String(value);
};

export const renderFileValue = (value: unknown): React.ReactNode => {
  if (isFileObject(value)) return renderFile(value);
  return String(value);
};

export const renderUrlValue = (value: unknown): React.ReactNode => {
  if (!isUrlValue(value)) return String(value);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <UrlValue value={value} />
    </div>
  );
};
